// apps/web/src/features/crm/createRequestFromFunnel.ts — cross-route
// "Create request" action for the CRM lead/deal detail drawers (#1599).
//
// WHY: a manager creates a China request FROM a lead/deal with the funnel
// link bound automatically. CRM cannot reach into the requests feature (no
// feature→feature import), so it SETS a neutral "open a fresh draft" intent on
// the `requestOpen` store and navigates to `/china-rates`. There,
// `RequestsListPage` CONSUMES the intent and opens the pre-bound draft on the
// requests feature's own `useRequestsStore` — the single #1594 draft state
// machine — without clobbering it on mount.
//
// This is STATE-driven (not a URL param) precisely because the store survives
// navigation and the list page never resets `draft` on mount — no re-open
// loop, no second create path, no parallel flags. The funnel link rides the
// FIRST save only (see `buildDraftSaveInput`, #1599/#1596).
import type { NavigateFunction } from 'react-router';
import { useRequestOpenStore } from '../../stores/index.js';

const CHINA_RATES_ROUTE = '/china-rates';

/**
 * Request a fresh China draft pre-bound to the given lead and/or deal, then
 * navigate to the request workspace so the user lands in the new draft.
 *
 * @aiInvariant (#1599, #1753) CRM only SETS the neutral `requestOpen` intent;
 *   `RequestsListPage` opens the draft on the requests store on arrival. The
 *   pre-bound `leadId`/`dealId` reach the wire on the FIRST save only — the
 *   save adapter gates them behind the same flag as the create-time
 *   `clientToken`. Passing neither (the standalone "+" path) opens an unlinked
 *   draft through the same store action.
 */
export function createRequestFromFunnel(
  navigate: NavigateFunction,
  link: { leadId?: string; dealId?: string },
): void {
  useRequestOpenStore.getState().requestFreshDraft(link);
  navigate(CHINA_RATES_ROUTE);
}
